import React from "react";
import { Pencil, SlidersHorizontal, Sparkles } from "lucide-react";

import { ProvenBadge, RAIL_HEAD_BLOCK, RAIL_HEADING } from "./TenderUI";

/* Discover's left rail: what Sensa matches against, said back plainly, with the
 * one place to change it (Tune — which also holds the new-match alert choice).
 * Read from the match profile, which is Memory plus the workspace's own edits —
 * so what is shown here is exactly what the matching used.
 *
 * Built like Search's rail (TenderRail): the same tinted block on top, the same
 * section titles and spacing, so switching modes changes the content, not the
 * look. */

// The Tune step that edits each section: 0 Company, 1 What you bid on.
const TUNE_STEP = { lines: 0, capabilities: 0, industries: 1, locations: 1 };

// A section opens Tune at the step that edits it. A div with button semantics,
// not a <button>: it holds lists and blocks, which a button may not contain.
const Section = ({ title, aside, onEdit, children }) => {
  const editable = onEdit
    ? {
        role: "button",
        tabIndex: 0,
        "aria-label": `Edit ${title.toLowerCase()} in Tune`,
        onClick: onEdit,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onEdit();
          }
        },
      }
    : {};
  return (
    <div
      {...editable}
      className={`group border-b border-[#F1F1F3] px-[18px] pb-3.5 pt-4 ${
        onEdit ? "cursor-pointer transition-colors hover:bg-[#FCF8FC] focus-visible:bg-[#FCF8FC] focus-visible:outline-none" : ""
      }`}
    >
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <span className={RAIL_HEADING}>{title}</span>
        <span className="flex items-center gap-1.5">
          {aside && <span className="font-outfit text-[11.5px] text-[#77707E]">{aside}</span>}
          {onEdit && (
            <Pencil
              aria-hidden
              className="h-3 w-3 text-[#BB42AE] opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
              strokeWidth={2.4}
            />
          )}
        </span>
      </div>
      {children}
    </div>
  );
};

const LineOfWork = ({ line }) => (
  <li className="flex items-center justify-between gap-2 px-3 py-2">
    <span className="min-w-0 truncate text-[13.5px] font-medium text-[#1A1A1A]" title={line.label}>
      {line.label}
    </span>
    <ProvenBadge proof={line.proof} />
  </li>
);

const Pill = ({ children, title }) => (
  <span
    title={title}
    className="inline-block max-w-full truncate rounded-lg border border-[#E7CDE4] bg-[#FBF2FA] px-2 py-[3px] text-[12.5px] text-[#7A3A73]"
  >
    {children}
  </span>
);

const Plain = ({ children }) => (
  <span className="inline-block rounded-lg border border-[#18101C]/10 bg-white px-2 py-[3px] text-[12.5px] text-[#3F3F46]">
    {children}
  </span>
);

const Value = ({ children }) => <p className="text-[13px] leading-snug text-[#1A1A1A]">{children}</p>;

export default function DiscoverRail({ profile, onTune, tips = [], onTip }) {
  const codes = profile ? profile.codes.filter((c) => c.selected) : [];
  const lines = profile ? profile.lines_of_work : [];
  const proven = lines.filter((l) => l.proof.length > 0).length;

  return (
    <div className="font-outfit">
      <div className={RAIL_HEAD_BLOCK}>
        <div className="flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 flex-none text-[#A04A97]" strokeWidth={2.2} />
          <span className={`${RAIL_HEADING} flex-1 whitespace-nowrap`}>Your match profile</span>
          <button
            type="button"
            onClick={() => onTune()}
            className="inline-flex flex-none items-center gap-1 text-[11.5px] font-semibold text-[#BB42AE] hover:underline"
          >
            <SlidersHorizontal className="h-3 w-3" strokeWidth={2.4} />
            Tune
          </button>
        </div>
        <p className="mt-1.5 text-[11.5px] leading-[1.45] text-[#71717A]">
          What Sensa looks for in every tender, from your Memory and past bids.
        </p>
      </div>

      {!profile ? (
        <div className="space-y-2 px-[18px] py-4">
          {[70, 50, 85, 40].map((w) => (
            <div key={w} className="h-3.5 animate-pulse rounded bg-[#18101C]/[0.06]" style={{ width: `${w}%` }} />
          ))}
        </div>
      ) : (
        <>
          {!!lines.length && (
            <Section
              title="Lines of work"
              aside={`${proven} of ${lines.length} proven`}
              onEdit={() => onTune(TUNE_STEP.lines)}
            >
              <ul className="divide-y divide-[#18101C]/[0.06] overflow-hidden rounded-lg border border-[#18101C]/[0.08] bg-white">
                {lines.map((l) => (
                  <LineOfWork key={l.label} line={l} />
                ))}
              </ul>
            </Section>
          )}

          <Section title="Industries" aside="NAICS / PSC" onEdit={() => onTune(TUNE_STEP.industries)}>
            <div className="flex flex-wrap gap-1.5">
              {codes.length ? (
                codes.slice(0, 6).map((c) => (
                  <Pill key={c.code} title={c.title}>
                    {c.code}
                    {c.title ? ` · ${c.title}` : ""}
                  </Pill>
                ))
              ) : (
                <span className="text-[12.5px] text-[#77707E]">Worked out on the first match run.</span>
              )}
              {codes.length > 6 && <Plain>+{codes.length - 6} more</Plain>}
            </div>
          </Section>

          {!!profile.capabilities.length && (
            <Section title="Capabilities" onEdit={() => onTune(TUNE_STEP.capabilities)}>
              <div className="flex flex-wrap gap-1.5">
                {profile.capabilities.slice(0, 6).map((c) => (
                  <Plain key={c}>{c}</Plain>
                ))}
                {profile.capabilities.length > 6 && <Plain>+{profile.capabilities.length - 6} more</Plain>}
              </div>
            </Section>
          )}

          <Section title="Locations" onEdit={() => onTune(TUNE_STEP.locations)}>
            <Value>{profile.states.join(", ") || "Anywhere"}</Value>
          </Section>
        </>
      )}

      {!!tips.length && (
        <div className="space-y-2 border-b border-[#F1F1F3] px-[18px] py-3.5">
          {tips.map((tip) => (
            <div key={`${tip.kind}-${tip.code}`} className="rounded-lg border border-[#F5C46B]/60 bg-[#FFF8E6] px-3 py-2.5">
              <p className="text-[12.5px] leading-snug text-[#7C4A03]">
                {tip.kind === "exclude_code" ? (
                  <>
                    You marked {tip.count} tenders under <b>{tip.code}</b>
                    {tip.title ? ` (${tip.title})` : ""} not your kind of work.
                  </>
                ) : (
                  <>
                    <b>{tip.code}</b>
                    {tip.title ? ` (${tip.title})` : ""} has {tip.count.toLocaleString()} open tender
                    {tip.count === 1 ? "" : "s"} next to your codes.
                  </>
                )}
              </p>
              <button
                type="button"
                onClick={() => onTip?.(tip)}
                className="mt-1.5 text-[12.5px] font-semibold text-[#8E4187] hover:underline"
              >
                {tip.kind === "exclude_code" ? "Stop matching this code" : "Add this code"}
              </button>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}
